import type { ChatNode } from '../types/chat';

/** Id of the corner chat's main menu — its options are the opening quick-picks */
export const DOCK_MENU = 'd-menu';

const ELSEWHERE = { label: 'Something else', next: DOCK_MENU, reaction: 'neutral' } as const;
const CONTACT = {
  label: 'Start a project',
  next: 'd-contact',
  reaction: 'surprised',
  // Glides straight down to the contact footer
  action: { type: 'scroll', target: '#contact' },
} as const;

/**
 * Pixel's corner chat: quick, helpful answers about the studio and its work. Short and plain.
 * Every topic has `again` lines, so the questions stay on offer and never repeat word for word.
 */
export const dockScript: ChatNode[] = [
  // ── Menu ──
  {
    id: DOCK_MENU,
    lines: [{ text: 'How can I help?', emotion: 'curious' }],
    again: [{ text: 'Anything else?', emotion: 'happy' }],
    options: [
      { label: 'What do you do?', next: 'd-what', reaction: 'happy' },
      { label: 'Show me projects', next: 'd-work', reaction: 'happy', action: { type: 'scroll', target: '#work' } },
      { label: 'What’s the cost?', next: 'd-cost', reaction: 'curious' },
      CONTACT,
    ],
  },

  // ── What the studio does ──
  {
    id: 'd-what',
    lines: [
      { text: 'We build 3D brand experiences for the web.', emotion: 'happy' },
      { text: '3D websites, product configurators, virtual worlds and web games.', emotion: 'curious' },
      { text: 'All in the browser. No app needed.', emotion: 'happy' },
    ],
    again: [{ text: '3D websites, configurators, worlds and games. In the browser.', emotion: 'happy' }],
    options: [
      { label: 'See all services', next: 'd-services', reaction: 'curious', action: { type: 'scroll', target: '#services' } },
      { label: 'Who do you work with?', next: 'd-clients', reaction: 'curious' },
      ELSEWHERE,
    ],
  },
  {
    id: 'd-services',
    lines: [
      { text: 'Here they are.', emotion: 'happy' },
      { text: 'Open any service to read more.', emotion: 'curious' },
    ],
    again: [{ text: 'Back to our services.', emotion: 'happy' }],
    options: [CONTACT, ELSEWHERE],
  },
  {
    id: 'd-clients',
    lines: [
      { text: 'Automotive, jewellery, real estate, tech and games.', emotion: 'curious' },
      { text: 'Big brands and small ones.', emotion: 'happy' },
    ],
    again: [{ text: 'Automotive, jewellery, real estate, tech and games.', emotion: 'curious' }],
    options: [
      { label: 'Show me projects', next: 'd-work', reaction: 'happy', action: { type: 'scroll', target: '#work' } },
      ELSEWHERE,
    ],
  },

  // ── Projects ──
  {
    id: 'd-work',
    lines: [
      { text: 'Here’s our work.', emotion: 'happy' },
      { text: 'Click any project to see it.', emotion: 'curious' },
    ],
    again: [{ text: 'Back to the projects.', emotion: 'happy' }],
    options: [CONTACT, ELSEWHERE],
  },

  // ── Cost & timing ──
  {
    id: 'd-cost',
    lines: [
      { text: 'Most projects start at €8,000–10,000.', emotion: 'neutral' },
      { text: 'Bigger ones go to €20,000 or more.', emotion: 'curious' },
      { text: 'A 3D site takes a few weeks. A full world, a few months.', emotion: 'happy' },
    ],
    again: [{ text: 'From €8,000. Weeks for a site, months for a world.', emotion: 'neutral' }],
    options: [
      { label: 'How do we start?', next: 'd-process', reaction: 'curious' },
      CONTACT,
      ELSEWHERE,
    ],
  },
  {
    id: 'd-process',
    lines: [
      { text: '1. A short call about your idea.', emotion: 'happy' },
      { text: '2. A concept and a clear quote.', emotion: 'curious' },
      { text: '3. We build it together.', emotion: 'happy' },
    ],
    again: [{ text: 'Call, concept and quote, then we build.', emotion: 'happy' }],
    options: [CONTACT, ELSEWHERE],
  },

  // ── Contact ──
  {
    id: 'd-contact',
    lines: [
      { text: 'Great! Write to hey@immersivestudios.de', emotion: 'happy' },
      { text: 'We reply within a day.', emotion: 'curious' },
    ],
    again: [{ text: 'hey@immersivestudios.de. We reply within a day.', emotion: 'happy' }],
    options: [
      { label: 'How do we start?', next: 'd-process', reaction: 'curious' },
      ELSEWHERE,
    ],
  },
];
